import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  TextField,
  MenuItem,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close,
  Download,
  ExpandMore,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import dayjs from "dayjs";
import customsService from "../../services/customsService";
import { useSnackbar } from "notistack";
import { formatUsd } from "../../utils/currency";

const REGIMES = [
  { value: "IM4", label: "IM4 — Permanent import (standard)" },
  { value: "IM4A", label: "IM4A — Permanent import (accompanied)" },
  { value: "IM7", label: "IM7 — Temporary import" },
  { value: "IM9", label: "IM9 — Re-import" },
];

const FINANCE_PARTIES = [
  { value: "T", label: "T — Trader" },
  { value: "A", label: "A — Agent / Broker" },
];

const TRANSPORT_MODES = [
  { value: "SEA", label: "SEA" },
  { value: "AIR", label: "AIR" },
];

const TERMS_OF_DELIVERY = [
  { value: "CIF", label: "CIF — Cost, Insurance & Freight" },
  { value: "FOB", label: "FOB — Free On Board" },
  { value: "EXW", label: "EXW — Ex Works" },
  { value: "CFR", label: "CFR — Cost & Freight" },
  { value: "DDU", label: "DDU — Delivered Duty Unpaid" },
];

const OUTPUT_FORMATS = [
  { value: "xml", label: "XML (.xml)" },
  { value: "xsd", label: "XSD (.xsd)" },
];

/**
 * CustomsOverridesDialog
 * Shows a review/edit dialog before downloading the SADEntry XML.
 * Allows admin to override regime, finance party, bill number etc.
 * Works for both single and bulk (multiple) shipment selections.
 */
const CustomsOverridesDialog = ({ open, shipments, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  // Shared overrides applied to all selected shipments
  const [shared, setShared] = useState({
    regime: "IM4",
    financeParty: "T",
    transportMode: "SEA",
    termsOfDelivery: "CIF",
    vesselName: "",
    sealNumber: "",
    importerTin: "",
  });
  const [outputFormat, setOutputFormat] = useState("xml");

  // Per-shipment overrides: { [shipmentId]: { billNumber, containerNumber } }
  const [perShipment, setPerShipment] = useState({});

  useEffect(() => {
    if (!open || !shipments?.length) return;
    // Pre-populate per-shipment overrides from shipment data
    const ps = {};
    shipments.forEach((s) => {
      ps[s._id] = {
        billNumber: s.trackingNumber || "",
        containerNumber: s.sailingSchedule?.containerNumber || "",
      };
    });
    setPerShipment(ps);
    // Pre-populate vessel name from first shipment's sailing
    if (shipments[0]?.sailingSchedule?.vesselName) {
      setShared((p) => ({
        ...p,
        vesselName: shipments[0].sailingSchedule.vesselName,
      }));
    }
  }, [open, shipments]);

  const setS = (f) => (e) => setShared((p) => ({ ...p, [f]: e.target.value }));
  const setPS = (id, f) => (e) =>
    setPerShipment((p) => ({ ...p, [id]: { ...p[id], [f]: e.target.value } }));

  const isSingle = shipments?.length === 1;
  const distinctCustomerCount = new Set(
    (shipments || []).map((s) => s.customer?._id || s.customer),
  ).size;
  const spansMultipleCustomers = distinctCustomerCount > 1;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const ids = shipments.map((s) => s._id);

      // Build overrides object: shared fields + per-shipment fields merged
      const overrides = {};
      shipments.forEach((s) => {
        overrides[s._id] = {
          ...shared,
          ...(perShipment[s._id] || {}),
        };
      });

      // For single, also pass as flat overrides (controller accepts both shapes)
      const payload = isSingle
        ? { ...shared, ...(perShipment[ids[0]] || {}) }
        : overrides;

      await customsService.generateAndDownload(ids, payload, outputFormat);

      enqueueSnackbar(
        isSingle
          ? `Customs ${outputFormat.toUpperCase()} downloaded successfully`
          : `Combined customs ${outputFormat.toUpperCase()} for ${shipments.length} shipments downloaded successfully`,
        { variant: "success" },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to generate customs XML",
        { variant: "error" },
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!shipments?.length) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={16}>
            Generate Customs Declaration
            {!isSingle && (
              <Chip
                label={`${shipments.length} shipments`}
                size="small"
                sx={{
                  ml: 1.5,
                  bgcolor: "info.light",
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Review and confirm fields before downloading the SADEntry XML
            {!isSingle && !spansMultipleCustomers ? " — all selected shipments combined into one file" : ""}
            {spansMultipleCustomers
              ? ` — spans ${distinctCustomerCount} customers, so one .xml per customer will be bundled into a .zip (a declaration can only have one importer)`
              : ""}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: 0.25 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Shipment summary */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "action.hover",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Selected Shipments
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {shipments.map((s) => (
              <Box
                key={s._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.5,
                }}
              >
                {s.customsDeclarationSubmitted ? (
                  <CheckCircle sx={{ fontSize: 13, color: "success.main" }} />
                ) : (
                  <Warning sx={{ fontSize: 13, color: "warning.main" }} />
                )}
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ fontFamily: "monospace" }}
                >
                  {s.trackingNumber}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  maxWidth={160}
                >
                  {s.packageDescription || s.vendor?.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          {/* Info notice */}
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            <Typography variant="caption">
              Fields are pre-filled from shipment data. Review and adjust as
              needed before downloading. The XML will be valid against the
              Cayman Islands COLS XSD schema.
            </Typography>
          </Alert>

          {/* ── SHARED FIELDS ─────────────────────────────────────────────── */}
          <Typography
            variant="overline"
            color="text.secondary"
            display="block"
            mb={1.5}
          >
            Declaration Settings {!isSingle && "(Applied to all)"}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 2.5,
            }}
          >
            <TextField
              select
              label="Customs Regime *"
              value={shared.regime}
              onChange={setS("regime")}
              size="small"
              helperText="IM4 for standard personal/commercial imports"
            >
              {REGIMES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Finance Party"
              value={shared.financeParty}
              onChange={setS("financeParty")}
              size="small"
              helperText="Party responsible for financial settlement"
            >
              {FINANCE_PARTIES.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Transport Mode"
              value={shared.transportMode}
              onChange={setS("transportMode")}
              size="small"
            >
              {TRANSPORT_MODES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Terms of Delivery"
              value={shared.termsOfDelivery}
              onChange={setS("termsOfDelivery")}
              size="small"
              helperText="Incoterms"
            >
              {TERMS_OF_DELIVERY.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Output Format"
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              size="small"
              helperText="Choose download extension"
            >
              {OUTPUT_FORMATS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Vessel Name"
              value={shared.vesselName}
              onChange={setS("vesselName")}
              size="small"
              placeholder="e.g. MV Cayman Voyager"
              helperText="Pre-filled from sailing schedule"
            />

            <TextField
              label="Importer TIN (optional)"
              value={shared.importerTin}
              onChange={setS("importerTin")}
              size="small"
              placeholder="e.g. CYM12345"
              helperText="Cayman Customs TIN — leave blank to use name"
            />
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* ── PER-SHIPMENT FIELDS ────────────────────────────────────────── */}
          <Typography
            variant="overline"
            color="text.secondary"
            display="block"
            mb={1.5}
          >
            Per-Shipment Details
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {shipments.map((s, idx) => (
              <Accordion
                key={s._id}
                defaultExpanded={isSingle}
                disableGutters
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ px: 2, minHeight: 48 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                      mr: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ fontFamily: "monospace", color: "primary.main" }}
                    >
                      {s.trackingNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {s.packageDescription || s.vendor?.name}
                    </Typography>
                    <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                      <Chip
                        label={formatUsd(s.declaredValue)}
                        size="small"
                        sx={{ fontSize: 10, height: 18, bgcolor: "action.selected" }}
                      />
                      <Chip
                        label={`${(s.items || []).length} item${(s.items || []).length !== 1 ? "s" : ""}`}
                        size="small"
                        sx={{ fontSize: 10, height: 18, bgcolor: "action.selected" }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="Bill of Lading / Tracking Number *"
                      value={perShipment[s._id]?.billNumber || ""}
                      onChange={setPS(s._id, "billNumber")}
                      size="small"
                      helperText="4–18 characters required by XSD"
                      inputProps={{ maxLength: 18 }}
                    />
                    <TextField
                      label="Container Number"
                      value={perShipment[s._id]?.containerNumber || ""}
                      onChange={setPS(s._id, "containerNumber")}
                      size="small"
                      placeholder="e.g. CAYM-042"
                      helperText="Pre-filled from sailing schedule"
                    />
                  </Box>

                  {/* Read-only summary */}
                  <Box sx={{ bgcolor: "action.hover", borderRadius: 1.5, p: 1.5 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 1,
                      }}
                    >
                      {[
                        {
                          label: "Customer",
                          value: `${s.customer?.firstName} ${s.customer?.lastName}`,
                        },
                        { label: "Vendor / Exporter", value: s.vendor?.name },
                        {
                          label: "Origin Country",
                          value: s.vendor?.country || "USA",
                        },
                        {
                          label: "Declared Value",
                          value: formatUsd(s.declaredValue),
                        },
                        {
                          label: "Departure Date",
                          value: s.sailingSchedule?.departureDate
                            ? dayjs(s.sailingSchedule.departureDate).format(
                                "MMM D, YYYY",
                              )
                            : "N/A",
                        },
                        {
                          label: "Arrival Date",
                          value: s.sailingSchedule?.estimatedArrivalDate
                            ? dayjs(
                                s.sailingSchedule.estimatedArrivalDate,
                              ).format("MMM D, YYYY")
                            : "N/A",
                        },
                      ].map((r) => (
                        <Box key={r.label}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {r.label}
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {r.value || "—"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Items list */}
                    {(s.items || []).length > 0 && (
                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            mb: 0.75,
                          }}
                        >
                          Items (HS codes auto-filled as 99999900 if not set)
                        </Typography>
                        {s.items.map((item, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              maxWidth={280}
                            >
                              {item.description}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              × {item.quantity} · $
                              {(
                                (item.quantity || 1) * (item.unitValue || 0)
                              ).toFixed(2)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {isSingle
            ? "Will download 1 .xml file"
            : spansMultipleCustomers
              ? `Will download 1 .zip with ${distinctCustomerCount} .xml files (one per customer)`
              : `Will download 1 .xml file combining all ${shipments.length} shipments`}
        </Typography>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={downloading}
          startIcon={downloading ? null : <Download />}
        >
          {downloading ? (
            <CircularProgress size={18} color="inherit" />
          ) : isSingle ? (
            "Download XML"
          ) : spansMultipleCustomers ? (
            `Download ZIP (${distinctCustomerCount} customers)`
          ) : (
            `Download Combined XML (${shipments.length})`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomsOverridesDialog;
